// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { type AskPunttOpenItem } from "@mg/schemas/src/christo/askPunttOpenItems";
import { Check } from "@phosphor-icons/react";
import { Button, Dialog, Popover, Text, TextField } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { AskV2OpenItemsGroups } from "./AskV2OpenItemsGroups";
import { AskV2OpenItemsSplit } from "./AskV2OpenItemsSplit";
import { AskV2ProjectDoneBadge } from "./AskV2ProjectDoneBadge";
import { openItemKindLabel } from "./openItemKindLabel";
import { openItemRowTitle, openItemShortNeeded } from "./openItemRowTitle";
import { listOpenItemAssignees } from "./openItemsApi";
import {
  formatOpenItemAge,
  type UseProjectOpenItemsOptions,
  useProjectOpenItems,
} from "./useProjectOpenItems";

import { ASK_V2_RAIL_SECTION_CLASS } from "../rail/AskV2RailSection";
type Member = {
  _id: string;
  name?: string | null;
  email?: string | null;
};

type AssigneeRow = { userId: string; name: string };

function assignedSuggestions(item: AskPunttOpenItem): string[] {
  if (item.kind !== "source_conflict") return [];
  if (!item.ownerUserId) return [];
  return (item.suggestions ?? []).map((label) => label.trim()).filter(Boolean);
}

function OpenItemSuggestions({ item }: { item: AskPunttOpenItem }) {
  const suggestions = assignedSuggestions(item);
  if (suggestions.length === 0) return null;
  return (
    <span className="mt-1 block">
      {suggestions.map((label, suggestionIndex) => (
        <span
          key={`${item.id}-${suggestionIndex}`}
          className="block text-[12px] leading-snug text-puntt-neutral-gray-11"
          data-testid={`ask-v2-open-item-suggestion-${item.id}-${suggestionIndex}`}
        >
          {label}
        </span>
      ))}
    </span>
  );
}

const MARK_DONE_WARNING_MAX_ROWS = 5;

function ownerLabel(item: AskPunttOpenItem, members: Member[]): string {
  const member = members.find((row) => row._id === item.ownerUserId);
  const fromName = member?.name?.trim() ?? "";
  const fromEmail = member?.email?.trim() ?? "";
  if (fromName.length > 0) return fromName;
  if (fromEmail.length > 0) return fromEmail;
  return item.ownerUserId ?? "";
}

function AssignOpenItemPopover({
  projectId,
  item,
  ownerDisplayName,
  open,
  pending,
  onOpenChange,
  onAssign,
}: {
  projectId: string;
  item: AskPunttOpenItem;
  ownerDisplayName: string;
  open: boolean;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onAssign: (ownerUserId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const triggerLabel =
    ownerDisplayName.length > 0
      ? ownerDisplayName
      : t("askV2.openItems.assign");
  const triggerAriaLabel =
    ownerDisplayName.length > 0
      ? t("askV2.openItems.changeOwnerAria", {
          needed: item.needed,
          owner: ownerDisplayName,
        })
      : t("askV2.openItems.assignAria", { needed: item.needed });
  const [search, setSearch] = useState("");
  const assigneesQuery = useQuery({
    queryKey: ["ask-puntt-open-item-assignees", projectId],
    queryFn: () => listOpenItemAssignees(projectId),
    enabled: open && projectId.length > 0,
  });
  const assignees = assigneesQuery.data?.assignees ?? [];
  // why: `data` is undefined until the roster lands, so an unguarded empty list
  // told every opener "there is nobody to assign on this project" while the
  // fetch was still in flight. `isLoading` is `isPending && isFetching`, so a
  // disabled query (closed popover, blank project) never sticks on loading.
  const assigneesLoading = assigneesQuery.isLoading;
  const needle = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      needle.length === 0
        ? assignees
        : assignees.filter((row) => row.name.toLowerCase().includes(needle)),
    [assignees, needle],
  );

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) setSearch("");
        onOpenChange(next);
      }}
    >
      <Popover.Trigger>
        <Button
          type="button"
          variant="ghost"
          size="1"
          data-testid={`ask-v2-open-item-assign-${item.id}`}
          aria-label={triggerAriaLabel}
          disabled={pending}
          className="!h-auto !justify-start !p-0 text-[12px] !font-normal leading-snug text-puntt-neutral-gray-10 underline-offset-2 hover:bg-transparent hover:underline"
        >
          {triggerLabel}
        </Button>
      </Popover.Trigger>
      <Popover.Content
        side="bottom"
        align="start"
        className="w-[280px] p-2"
        data-testid={`ask-v2-open-item-assign-popover-${item.id}`}
      >
        <TextField.Root
          size="2"
          className="mb-2"
          data-testid={`ask-v2-open-item-assign-search-${item.id}`}
          placeholder={t("askV2.openItems.assignSearchPlaceholder")}
          aria-label={t("askV2.openItems.assignSearchPlaceholder")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {assigneesLoading ? (
          <Text size="2" data-testid="ask-v2-open-item-assign-loading">
            {t("loading")}
          </Text>
        ) : assignees.length === 0 ? (
          <Text size="2" data-testid="ask-v2-open-item-assign-empty">
            {t("askV2.openItems.assignEmpty")}
          </Text>
        ) : filtered.length === 0 ? (
          <Text size="2" data-testid="ask-v2-open-item-assign-no-match">
            {t("askV2.openItems.assignNoMatch")}
          </Text>
        ) : (
          <ul className="flex max-h-[240px] flex-col gap-0.5 overflow-y-auto">
            {filtered.map((row: AssigneeRow) => {
              const isOwner = item.ownerUserId === row.userId;
              return (
                <li key={row.userId} className="list-none">
                  <button
                    type="button"
                    data-testid={`ask-v2-open-item-expert-${row.userId}`}
                    aria-current={isOwner ? "true" : undefined}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-puntt-neutral-gray-3"
                    onClick={() => {
                      onAssign(row.userId);
                      setSearch("");
                      onOpenChange(false);
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">{row.name}</span>
                    {isOwner ? (
                      <Check
                        size={14}
                        className="text-puntt-brand-purple-9 shrink-0"
                        aria-hidden
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

function OpenItemRows({
  projectId,
  items,
  members,
  mayAssign,
  onAssign,
  assignPending,
  onNudge,
  nudgePendingItemId,
  nudgedItemIds,
  onAttach,
}: {
  projectId: string;
  items: AskPunttOpenItem[];
  members: Member[];
  mayAssign: boolean;
  onAssign: (itemId: string, ownerUserId: string) => void;
  assignPending: boolean;
  onNudge: (itemId: string) => void;
  nudgePendingItemId: string | null;
  nudgedItemIds: ReadonlySet<string>;
  onAttach: (item: AskPunttOpenItem) => void;
}) {
  const { t } = useTranslation("ui");
  const [assignOpenItemId, setAssignOpenItemId] = useState<string | null>(null);
  if (items.length === 0) return null;
  return (
    <>
      {items.map((item, index) => {
        const owner = ownerLabel(item, members);
        const age = formatOpenItemAge(item.openedAt, new Date());
        // why: every viewer reassigns via AssignOpenItemPopover; owner is not duplicated in meta
        const showMeta = age.length > 0 || (item.fileName?.length ?? 0) > 0;
        const title = openItemRowTitle(item);
        const question = openItemShortNeeded(item);
        const suggestions = assignedSuggestions(item);
        const suggestionLabel =
          suggestions.length > 0 ? `, ${suggestions.join(", ")}` : "";
        const ariaQuestion =
          title.length > 0 && title !== question
            ? `${title}. ${question}`
            : question;
        return (
          <Fragment key={item.id}>
            {index > 0 ? (
              <li
                aria-hidden="true"
                data-testid={`ask-v2-open-item-divider-${item.id}`}
                className="list-none border-t border-puntt-neutral-gray-6"
              />
            ) : null}
            <li className="flex list-none flex-col gap-1 py-2">
              <button
                type="button"
                data-testid={`ask-v2-open-item-${item.id}`}
                aria-label={`${ariaQuestion}${suggestionLabel}, ${owner}`}
                onClick={() => onAttach(item)}
                className="focus-visible:outline-puntt-brand-purple-9 flex w-full items-start gap-2 rounded-md bg-transparent text-left text-[13px] text-puntt-neutral-gray-12 hover:bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ backgroundColor: "transparent" }}
              >
                <span className="min-w-0">
                  <span
                    className="block text-[13px] font-medium leading-snug text-puntt-neutral-gray-12"
                    data-testid={`ask-v2-open-item-title-${item.id}`}
                  >
                    {title}
                  </span>
                  {/* why: this line is gated on `question`, so it must render
                      `question` too. `item.needed` is the raw payload — on an
                      unjudged finding that is the `**Issue:** ... **Fix:** ...`
                      markdown body `openItemShortNeeded` strips, so the row
                      printed the markers and the remediation while its own
                      accessible name carried the cleaned claim. */}
                  {title !== question ? (
                    <span className="block leading-snug">{question}</span>
                  ) : null}
                  <OpenItemSuggestions item={item} />
                  {showMeta ? (
                    <span className="block text-[12px] leading-snug text-puntt-neutral-gray-10">
                      <span data-testid={`ask-v2-open-item-age-${item.id}`}>
                        {age}
                      </span>
                      {item.fileName ? ` · ${item.fileName}` : ""}
                    </span>
                  ) : null}
                </span>
              </button>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] leading-snug text-puntt-neutral-gray-10">
                <AssignOpenItemPopover
                  projectId={projectId}
                  item={item}
                  ownerDisplayName={owner}
                  open={assignOpenItemId === item.id}
                  pending={assignPending}
                  onOpenChange={(next) => {
                    setAssignOpenItemId(next ? item.id : null);
                  }}
                  onAssign={(ownerUserId) => {
                    onAssign(item.id, ownerUserId);
                  }}
                />
                {mayAssign ? (
                  <>
                    <span
                      aria-hidden="true"
                      className="text-puntt-neutral-gray-8"
                    >
                      ·
                    </span>
                    {nudgedItemIds.has(item.id) ? (
                      <span
                        data-testid={`ask-v2-open-item-nudged-${item.id}`}
                        aria-label={t("askV2.openItems.nudged")}
                        className="shrink-0 text-[12px] leading-snug text-puntt-neutral-gray-10"
                      >
                        {t("askV2.openItems.nudged")}
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="1"
                        data-testid={`ask-v2-open-item-nudge-${item.id}`}
                        aria-label={`Nudge ${owner} about ${item.needed}`}
                        disabled={
                          nudgePendingItemId === item.id ||
                          (item.ownerUserId?.length ?? 0) === 0
                        }
                        className="!h-auto shrink-0 !justify-start !p-0 text-[12px] !font-normal leading-snug text-puntt-neutral-gray-10 underline-offset-2 hover:bg-transparent hover:underline"
                        onClick={() => {
                          onNudge(item.id);
                        }}
                      >
                        {t("askV2.openItems.nudge")}
                      </Button>
                    )}
                  </>
                ) : null}
              </div>
            </li>
          </Fragment>
        );
      })}
    </>
  );
}

function MarkDoneWarning({
  open,
  items,
  members,
  pending,
  onCancel,
  onOverride,
}: {
  open: boolean;
  items: AskPunttOpenItem[];
  members: Member[];
  pending: boolean;
  onCancel: () => void;
  onOverride: () => void;
}) {
  const { t } = useTranslation("ui");
  const visible = items.slice(0, MARK_DONE_WARNING_MAX_ROWS);
  const remainder = items.length - visible.length;
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <Dialog.Content data-testid="ask-v2-mark-project-done-warning">
        <Dialog.Title>
          {t("askV2.openItems.warningTitle", { count: items.length })}
        </Dialog.Title>
        <Dialog.Description>
          {t("askV2.openItems.warningDescription")}
        </Dialog.Description>
        <ul className="my-3 flex flex-col gap-2">
          {visible.map((item) => {
            const kindLabel = openItemKindLabel(item.kind, t);
            const needed = openItemShortNeeded(item);
            const owner = ownerLabel(item, members);
            return (
              <li
                key={item.id}
                data-testid={`ask-v2-open-done-item-${item.id}`}
                aria-label={`${kindLabel}: ${needed}, ${owner}`}
                className="list-none text-[13px] text-puntt-neutral-gray-12"
              >
                <span className="block text-[11px] font-medium uppercase tracking-wide text-puntt-neutral-gray-10">
                  {kindLabel}
                </span>
                <span className="block leading-snug">{needed}</span>
                {owner.length > 0 ? (
                  <span className="block text-[12px] leading-snug text-puntt-neutral-gray-10">
                    {owner}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
        {remainder > 0 ? (
          <Text
            size="2"
            data-testid="ask-v2-open-done-more"
            className="mb-3 text-puntt-neutral-gray-11"
          >
            {t("askV2.openItems.warningMore", { count: remainder })}
          </Text>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="soft"
            color="gray"
            data-testid="ask-v2-mark-project-done-cancel"
            onClick={onCancel}
          >
            {t("askV2.openItems.cancel")}
          </Button>
          <Button
            type="button"
            data-testid="ask-v2-mark-project-done-override"
            disabled={pending}
            onClick={onOverride}
          >
            {t("askV2.openItems.override")}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function AskV2OpenItems({
  projectId,
  members,
  onAttachOpenItem,
  reviewHasRun = false,
  projectOpenItemsOptions,
  currentUserId,
  railHost,
  splitHost,
  onOpenSplit,
  onCloseSplit,
}: {
  projectId: string;
  members: Member[];
  onAttachOpenItem: (item: AskPunttOpenItem) => void;
  reviewHasRun?: boolean;
  projectOpenItemsOptions?: UseProjectOpenItemsOptions;
  currentUserId: string | undefined;
  railHost: HTMLElement | null;
  splitHost: HTMLElement | null;
  onOpenSplit: () => void;
  onCloseSplit: () => void;
}) {
  const { t } = useTranslation("ui");
  const { query, mark, assign, nudge } = useProjectOpenItems(
    projectId,
    projectOpenItemsOptions,
  );
  const openItems = (query.data?.openItems ?? []).filter(
    (item) => item.status !== "cleared",
  );
  useEffect(() => {
    if (!query.isSuccess) return;
    if (splitHost != null && openItems.length === 0) onCloseSplit();
  }, [query.isSuccess, splitHost, openItems.length, onCloseSplit]);
  const [nudgePendingItemId, setNudgePendingItemId] = useState<string | null>(
    null,
  );
  const [nudgedItemIds, setNudgedItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  const canMarkProjectDone = query.data?.canMarkProjectDone === true;
  const mayAssignOpenItems = query.data?.mayAssignOpenItems === true;
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningList, setWarningList] = useState<AskPunttOpenItem[]>([]);

  if (!query.isSuccess) return null;

  const items = openItems;
  const doneAt = query.data.doneAt;
  const doneBadge = (
    <AskV2ProjectDoneBadge doneAt={doneAt} testId="ask-v2-project-done-badge" />
  );

  // why: before a review finishes, an empty rollup is not "no open items" —
  // the list has not been produced yet. Hide the section until then.
  if (items.length === 0 && !warningOpen && !reviewHasRun) {
    // why: returning null here drops the done badge once every row is cleared.
    // Portal only: this component sits outside the sidebar, and an inline
    // badge would render above the chat.
    return railHost != null ? createPortal(doneBadge, railHost) : null;
  }

  async function onMark() {
    setWarningList(items);
    setWarningOpen(true);
  }

  async function onOverride() {
    await mark.mutateAsync(true);
    setWarningOpen(false);
  }

  // why: the count button is the control — a section toggle repeats "Open items" and hides the only action.
  const railSection =
    items.length > 0 || reviewHasRun ? (
      <div
        className={`${ASK_V2_RAIL_SECTION_CLASS} px-4 py-3`}
        data-testid="ask-v2-open-items-rail"
      >
        {items.length === 0 ? (
          <>
            {doneBadge}
            <Text size="2" data-testid="ask-v2-open-items-empty">
              {t("askV2.openItems.empty")}
            </Text>
          </>
        ) : (
          <div className="flex flex-col gap-3">
            {doneAt != null ? doneBadge : null}
            <Button
              type="button"
              variant="soft"
              className="w-full"
              data-testid="ask-v2-open-items-cta"
              onClick={onOpenSplit}
            >
              {t("askV2.openItems.listName")}{" "}
              <span data-testid="ask-v2-open-items-cta-count">
                {items.length}
              </span>
            </Button>
          </div>
        )}
      </div>
    ) : null;

  return (
    <>
      {railHost != null && railSection != null
        ? createPortal(railSection, railHost)
        : null}
      {splitHost != null && items.length > 0
        ? createPortal(
            <AskV2OpenItemsSplit onClose={onCloseSplit}>
              {doneAt != null ? doneBadge : null}
              {doneAt == null && canMarkProjectDone ? (
                <Button
                  type="button"
                  variant="soft"
                  data-testid="ask-v2-mark-project-done"
                  aria-label={t("askV2.openItems.markDone")}
                  disabled={mark.isPending}
                  onClick={() => {
                    void onMark();
                  }}
                  className="mb-3"
                >
                  {t("askV2.openItems.markDone")}
                </Button>
              ) : null}
              <AskV2OpenItemsGroups
                items={items}
                currentUserId={currentUserId}
                renderRows={(rows) => (
                  <OpenItemRows
                    projectId={projectId}
                    items={rows}
                    members={members}
                    mayAssign={mayAssignOpenItems}
                    assignPending={assign.isPending}
                    nudgePendingItemId={nudgePendingItemId}
                    nudgedItemIds={nudgedItemIds}
                    onAssign={(itemId, ownerUserId) => {
                      void assign.mutateAsync({ itemId, ownerUserId });
                    }}
                    onNudge={(itemId) => {
                      setNudgePendingItemId(itemId);
                      void nudge
                        .mutateAsync(itemId)
                        .then(() => {
                          setNudgedItemIds((current) => {
                            const next = new Set(current);
                            next.add(itemId);
                            return next;
                          });
                        })
                        .finally(() => {
                          setNudgePendingItemId((current) =>
                            current === itemId ? null : current,
                          );
                        });
                    }}
                    onAttach={onAttachOpenItem}
                  />
                )}
              />
            </AskV2OpenItemsSplit>,
            splitHost,
          )
        : null}
      <MarkDoneWarning
        open={warningOpen}
        items={warningList}
        members={members}
        pending={mark.isPending}
        onCancel={() => setWarningOpen(false)}
        onOverride={() => {
          void onOverride();
        }}
      />
    </>
  );
}
